import type { Key, ReactNode } from 'react';
import {
  Collection,
  ComboBox,
  Header,
  Item,
  ListBox,
  Popover,
  Section,
} from 'react-aria-components';

import type { TextFieldProps } from '../../../DesignSystem/Inputs/TextField';
import { TextField } from '../../../DesignSystem/Inputs/TextField';
import { classNames } from '../../../DesignSystem/Utils/classNames';

type SuggestionOption = {
  displayValue: ReactNode;
  id: string;
  textValue: string;
};

type SuggestionSection = {
  id: string;
  header?: ReactNode;
  options: SuggestionOption[];
};

export type AutocompleteSearchFieldProps = TextFieldProps & {
  isOpen: boolean;
  onSelectionChange: (key: Key) => void;
  suggestions: SuggestionSection[];
};

export function AutocompleteSearchField(props: AutocompleteSearchFieldProps) {
  const { isOpen, onSelectionChange, ref, suggestions, ...inputProps } = props;

  return (
    <ComboBox
      allowsCustomValue
      aria-label={inputProps.label}
      items={suggestions}
      menuTrigger="focus"
      onSelectionChange={onSelectionChange}
    >
      <TextField shouldUseReactAriaInput {...inputProps} />
      <Popover
        className="w-[var(--trigger-width)] rounded-lg bg-gray-1 shadow-lg-down ring-1 ring-gray-10 ring-opacity-5"
        containerPadding={0}
        isOpen={isOpen}
        shouldFlip={false}
      >
        <ListBox className="my-2">
          {(section: SuggestionSection) => (
            <Section key={section.id}>
              {section.header && (
                <Header className="mx-4 my-0.5">{section.header}</Header>
              )}
              <Collection items={section.options}>
                {(item) => (
                  <Item
                    className={({ isFocused, isHovered, isPressed }) =>
                      classNames('flex cursor-pointer items-center px-4 py-2', {
                        'bg-gray-3': isFocused || isHovered || isPressed,
                      })
                    }
                    id={item.id}
                    key={item.id}
                    textValue={item.textValue}
                  >
                    {item.displayValue}
                  </Item>
                )}
              </Collection>
            </Section>
          )}
        </ListBox>
      </Popover>
    </ComboBox>
  );
}
